// src/get-aspect-ratio.ts
var aspectRatios = ["9/16", "3/4", "1/1", "4/3", "16/9"];
function getAspectRatio(width, height) {
  const originalRatio = width / height;
  switch (true) {
    case originalRatio < 0.66:
      return "9/16";
    case originalRatio < 0.875:
      return "3/4";
    case originalRatio < 1.125:
      return "1/1";
    case originalRatio < 1.66:
      return "4/3";
    default:
      return "16/9";
  }
}

// src/index.ts
var GridObserver = class {
  nextId = 0;
  groupIndex = 0;
  _gridItemPositions = null;
  abortController = new AbortController();
  initializedItemCount = 0;
  loadedItemCount = 0;
  erroredItemCount = 0;
  columnCount = 0;
  mountedIds = /* @__PURE__ */ new Set();
  focusId = null;
  isFocusable(el) {
    return el.tabIndex !== void 0 && el.tabIndex > -1;
  }
  initGridItems(children) {
    const isTest = typeof process !== "undefined" && process.env.VITEST_WORKER_ID;
    const groupIndex = this.groupIndex++;
    const gridItems = children.map((child, i) => {
      const seed = Math.floor(Math.random() * (aspectRatios.length - 1));
      const ar = isTest ? "1/1" : aspectRatios[seed] ?? aspectRatios[0];
      return {
        id: `masonry-item-${this.nextId++}`,
        mounted: false,
        loaded: false,
        node: child,
        aspectRatio: ar,
        groupIndex,
        groupItemIndex: i
      };
    });
    this.initializedItemCount += gridItems.length;
    return gridItems;
  }
  resizeGridItem(item, rowGap) {
    const firstElementChild = item.children[0];
    if (!firstElementChild) {
      console.warn("No child element found in grid item.");
      return;
    }
    const { height } = firstElementChild.getBoundingClientRect();
    const id = item.dataset.masonryItem ?? "";
    const rowSpan = Math.floor(height) + rowGap;
    item.style.gridRowEnd = `span ${rowSpan}`;
    const gridPosition = this.gridItemPositions?.[id];
    if (gridPosition) {
      item.style.setProperty("--row-index", gridPosition.row.toString());
    }
  }
  resizeGridItems(gridEl, styleProxyEl) {
    const { rowGap, columnCount } = this.getGridStyles(styleProxyEl);
    const gridItemEls = gridEl.querySelectorAll(
      "[data-masonry-item]"
    );
    if (columnCount !== this.columnCount || // Grid columns count has changed
    !this.gridItemPositions) {
      this.columnCount = columnCount;
      this.updateGridItemPositions(gridItemEls);
    }
    gridItemEls.forEach((item) => {
      this.resizeGridItem(item, rowGap);
    });
  }
  handleGridItemsUpdate(items, handleImageLoad) {
    const newItems = items.filter((item) => {
      const isMounted = this.mountedIds.has(item.id);
      return !isMounted;
    });
    console.log("[MASONRY GRID] Grid updated. New items: ", newItems);
    if (!newItems.length) return;
    const lastId = items[items.length - newItems.length - 1]?.id;
    if (lastId) this.focusId = lastId;
    for (const item of newItems) {
      const gridItemEl = document.querySelector(
        `[data-masonry-item="${item.id}"]`
      );
      const img = gridItemEl?.querySelector("img");
      if (!gridItemEl) {
        console.warn(`Grid item element not found: ${item.id}`);
        continue;
      }
      if (img?.complete) {
        handleImageLoad();
      } else if (img) {
        const { signal } = this.abortController;
        img.addEventListener("load", () => handleImageLoad(), { signal });
        const handleError = () => this.handleImageError(item.id, img.src);
        img.addEventListener("error", handleError, { signal });
      }
      this.mountedIds.add(item.id);
    }
    if (this.focusId) {
      const focusEl = document.querySelector(
        `[data-masonry-item="${this.focusId}"]`
      );
      if (focusEl) this.updateFocus(focusEl);
    }
  }
  getGridStyles(gridEl) {
    const { gridTemplateColumns, rowGap, columnGap } = getComputedStyle(gridEl);
    return {
      columnCount: gridTemplateColumns.split(" ")?.length ?? 3,
      rowGap: parseInt(rowGap),
      columnGap: parseInt(columnGap)
    };
  }
  setImageAspectRatios(items) {
    return items.reduce((acc, item) => {
      const img = document.querySelector(`[data-masonry-item="${item.id}"]`)?.querySelector("img");
      if (img?.naturalHeight) {
        const aspectRatio = getAspectRatio(img.naturalWidth, img.naturalHeight);
        return [...acc, { ...item, loaded: true, aspectRatio }];
      }
      return acc;
    }, []);
  }
  handleImageError(id, src) {
    console.warn(`Image failed to load: ${id}, ${src}`);
    this.incrementProcessedItemCount(1, true);
  }
  updateGridItemPositions(gridItemEls) {
    const items = Array.from(gridItemEls);
    const columnIndices = {};
    let columnCounter = 0;
    const orderedItems = items.reduce((acc, item) => {
      const id = item.dataset.masonryItem;
      if (!id) return acc;
      const left = Math.round(item.getBoundingClientRect().left);
      if (!(left in columnIndices)) columnIndices[left] = columnCounter++;
      const col = columnIndices[left] ?? 0;
      const row = Object.entries(acc).filter(
        (entry) => entry[1].col === col
      ).length;
      return {
        ...acc,
        [id]: { row, col, index: row * columnCounter + col }
      };
    }, {});
    this._gridItemPositions = orderedItems;
  }
  updateFocus(el) {
    const focusableChildren = [
      ...Array.from(
        el.querySelectorAll(
          "a, button, input, select, textarea, [tabindex]"
        )
      )
    ];
    const focusableChild = focusableChildren.find(
      (element) => this.isFocusable(element)
    );
    if (focusableChild) focusableChild.focus({ preventScroll: true });
    this.focusId = null;
  }
  incrementProcessedItemCount(n = 1, isError = false) {
    if (isError) this.erroredItemCount += n;
    else this.loadedItemCount += n;
  }
  reset() {
    this.initializedItemCount = 0;
    this.loadedItemCount = 0;
    this.erroredItemCount = 0;
    this.groupIndex = 0;
  }
  destroy() {
    this.mountedIds.clear();
    this._gridItemPositions = null;
    this.nextId = 0;
    this.initializedItemCount = 0;
    this.loadedItemCount = 0;
    this.erroredItemCount = 0;
    this.columnCount = 0;
    this.focusId = null;
    this.abortController.abort();
  }
  set processedItemCount(value) {
    this.loadedItemCount = value;
  }
  get processedItemCount() {
    return this.loadedItemCount + this.erroredItemCount;
  }
  get gridItemPositions() {
    return this._gridItemPositions;
  }
};
var index_default = GridObserver;
export {
  GridObserver,
  index_default as default,
  getAspectRatio
};
